import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { BOUSALA_PRODUCTS } from "@/lib/bousala/products";
import {
  SMART_SECTION_KEYS,
  type SmartOrderStatus,
  type SmartSectionKey,
} from "@/lib/bousala/smart-order";
import { asReportLanguage } from "@/lib/bousala/smart-intake-schema";
import { tMajor } from "@/lib/bousala/i18n";
import { resolveAccountEmailCore } from "@/lib/bousala-purchase.functions";

/**
 * Buyer-side state for /portal/smart.
 *
 * SECURITY: the client never sends an order id. The caller's own order is
 * resolved from the session only (auth.uid() → profiles.id →
 * smart_reports.client_id, newest first) — the same pattern as
 * getSmartIntakeContext. Reads go through the CALLER's Supabase client, so
 * RLS is the enforcement: report sections are readable only once the order
 * status is `sent`. The UI never decides visibility on its own, and this
 * module never selects sections for any other status.
 *
 * The one admin read is the pending-transfer lookup: pending_purchases is
 * staff-only by policy, and the row is matched on the session's own email.
 */

export type SmartOrderSummary = {
  reportLanguage: "ar" | "en";
  targetCountry: string | null;
  financialContext: string | null;
  familyExpectations: string | null;
  futureGoals: string | null;
  concerns: string | null;
  majors: string[];
};

export type SmartReportHeaderData = {
  reportLanguage: "ar" | "en";
  firstName: string;
  code: string;
  sentAt: string | null;
  targetCountry: string | null;
  majors: string[];
};

export type SmartPortalState =
  | { kind: "none" }
  | { kind: "transfer_pending" }
  | { kind: "transfer_rejected" }
  | { kind: "awaiting_assessment" }
  | { kind: "intake" }
  | { kind: "preparing"; slaDueAt: string | null; summary: SmartOrderSummary }
  | {
      kind: "report";
      header: SmartReportHeaderData;
      sections: Partial<Record<SmartSectionKey, string>>;
    }
  | { kind: "closed"; status: Extract<SmartOrderStatus, "refunded" | "stalled"> };

export type SmartUiProfile = { preferredLanguage: "ar" | "en" };

type OrderRow = {
  id: string;
  status: SmartOrderStatus;
  attempt_id: string | null;
  sla_due_at: string | null;
  sent_at: string | null;
  target_country: string | null;
  financial_context: string | null;
  family_expectations: string | null;
  future_goals: string | null;
  concerns: string | null;
  report_language: string | null;
};

const ORDER_COLUMNS =
  "id, status, attempt_id, sla_due_at, sent_at, target_country, financial_context, family_expectations, future_goals, concerns, report_language";

async function readMajorNames(
  user: any,
  admin: any,
  smartReportId: string,
  lang: "ar" | "en" = "ar",
): Promise<string[]> {
  const { data: picked } = await user
    .from("smart_report_majors")
    .select("major_id, sort_order")
    .eq("smart_report_id", smartReportId)
    .order("sort_order");
  const ids = (picked ?? []).map((r: any) => r.major_id);
  if (ids.length === 0) return [];
  // ids come from the caller's RLS-gated picks; majors has no direct read policy.
  const { data: majors } = await admin.from("majors").select("id, slug, name_ar").in("id", ids);
  const byId = new Map(
    (majors ?? []).map((m: any) => [
      m.id,
      lang === "en" ? tMajor(m.slug, "en").name : (m.name_ar as string),
    ]),
  );
  return ids.map((id: string) => byId.get(id)).filter(Boolean) as string[];
}

/** Pending / rejected local transfer waiting on review, matched on the session email. */
async function readPendingTransfer(
  admin: any,
  email: string | null,
): Promise<SmartPortalState | null> {
  if (!email) return null;
  const { data: product } = await admin
    .from("products")
    .select("id")
    .eq("slug", BOUSALA_PRODUCTS.smart.slug)
    .maybeSingle();
  if (!product?.id) return null;

  const { data: row } = await admin
    .from("pending_purchases")
    .select("id, status")
    .eq("product_id", product.id)
    .eq("email", email.trim().toLowerCase())
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();
  if (!row) return null;
  if (row.status === "pending") return { kind: "transfer_pending" };
  if (row.status === "rejected") return { kind: "transfer_rejected" };
  return null;
}

export async function loadSmartPortalStateCore(
  user: any,
  admin: any,
  userId: string,
  email: string | null,
): Promise<SmartPortalState> {
  const { data: profile } = await user
    .from("profiles")
    .select("id, full_name")
    .eq("user_id", userId)
    .maybeSingle();

  const order: OrderRow | null = profile?.id
    ? (
        await user
          .from("smart_reports")
          .select(ORDER_COLUMNS)
          .eq("client_id", profile.id)
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle()
      ).data ?? null
    : null;

  if (!order) {
    return (await readPendingTransfer(admin, email)) ?? { kind: "none" };
  }

  switch (order.status) {
    case "payment_pending_review":
      return { kind: "transfer_pending" };
    case "paid":
      return { kind: "awaiting_assessment" };
    case "assessment_complete":
      return { kind: "intake" };
    case "refunded":
    case "stalled":
      return { kind: "closed", status: order.status };
    case "intake_complete":
    case "generating":
    case "ready_for_review": {
      // Deliberately no section read here: drafts must never reach the buyer.
      return {
        kind: "preparing",
        slaDueAt: order.sla_due_at,
        summary: {
          reportLanguage: asReportLanguage(order.report_language),
          targetCountry: order.target_country,
          financialContext: order.financial_context,
          familyExpectations: order.family_expectations,
          futureGoals: order.future_goals,
          concerns: order.concerns,
          majors: await readMajorNames(user, admin, order.id),
        },
      };
    }
    case "sent": {
      const { data: rows } = await user
        .from("smart_report_sections")
        .select("section_key, body_md")
        .eq("smart_report_id", order.id);
      const sections: Partial<Record<SmartSectionKey, string>> = {};
      for (const r of rows ?? []) {
        const key = r.section_key as SmartSectionKey;
        if (SMART_SECTION_KEYS.includes(key) && (r.body_md ?? "").trim()) {
          sections[key] = r.body_md as string;
        }
      }

      let code = "";
      if (order.attempt_id) {
        const { data: attempt } = await user
          .from("attempts")
          .select("scores")
          .eq("id", order.attempt_id)
          .maybeSingle();
        code = (attempt?.scores as any)?.holland_code ?? "";
      }

      const fullName: string = profile?.full_name ?? "";
      const reportLanguage = asReportLanguage(order.report_language);
      return {
        kind: "report",
        header: {
          reportLanguage,
          firstName: fullName.trim().split(/\s+/)[0] ?? "",
          code,
          sentAt: order.sent_at,
          targetCountry: order.target_country,
          majors: await readMajorNames(user, admin, order.id, reportLanguage),
        },
        sections,
      };
    }
    default:
      return { kind: "none" };
  }
}

export const getSmartPortalState = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<SmartPortalState> => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    // The access token can still be the pre-email anonymous one right after
    // the add-email step (same reason receipt submission reads the account
    // record). Resolve the buyer's email from the auth admin record so the
    // pending-transfer lookup matches the receipt they just filed — the claim
    // alone can be empty and would fall back to the empty "none" state.
    const claimsEmail = (context.claims as { email?: unknown })?.email;
    const email = await resolveAccountEmailCore(supabaseAdmin, context.userId, claimsEmail);
    return loadSmartPortalStateCore(context.supabase, supabaseAdmin, context.userId, email || null);
  });

export const getSmartUiProfile = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<SmartUiProfile> => {
    const { data } = await context.supabase
      .from("profiles")
      .select("preferred_language")
      .eq("user_id", context.userId)
      .maybeSingle();
    return { preferredLanguage: data?.preferred_language === "en" ? "en" : "ar" };
  });
